import { AO_HOST } from "../client/aoHost";
import { request } from "../services/request.js";
import fileExists from "../utils/fileExists";
import { client } from "../client";

// Standard IC positions, in AO2's dropdown order, each with its modern image
// stem and the legacy filename AO2 also accepts.
const STANDARD_POSITIONS = [
  { pos: "def", label: "Defense", legacy: "defenseempty" },
  { pos: "pro", label: "Prosecution", legacy: "prosecutorempty" },
  { pos: "hld", label: "Helper Defense", legacy: "helperstand" },
  { pos: "hlp", label: "Helper Prosecution", legacy: "prohelperstand" },
  { pos: "wit", label: "Witness", legacy: "witnessempty" },
  { pos: "jud", label: "Judge", legacy: "judgestand" },
  { pos: "jur", label: "Jury", legacy: "jurystand" },
  { pos: "sea", label: "Seance", legacy: "seancestand" },
];

interface PosOption {
  pos: string;
  label: string;
  custom?: boolean;
}

// Computed option lists are cached per background so we probe at most once per
// room (each probe is an HTTP request, and a background change shouldn't fire
// dozens of them every time).
const cache = new Map<string, PosOption[]>();

const imageExists = async (
  folder: string,
  stems: string[],
): Promise<boolean> => {
  for (const stem of stems) {
    for (const ext of client.background_extensions) {
      if (await fileExists(`${folder}${stem}${ext}`)) return true;
    }
  }
  return false;
};

const parseCustomPositions = (iniText: string): PosOption[] => {
  let positionsLine = "";
  for (const raw of iniText.split(/\r?\n/)) {
    const m = raw.trim().match(/^positions\s*=\s*(.*)$/i);
    if (m) {
      positionsLine = m[1];
      break;
    }
  }
  if (!positionsLine) return [];

  const standard = new Set(STANDARD_POSITIONS.map((p) => p.pos));
  const out: PosOption[] = [];
  for (const entry of positionsLine.split(",")) {
    const trimmed = entry.trim();
    if (!trimmed) continue;
    const realPos = trimmed.split(":")[0].trim();
    const label = trimmed.includes(":")
      ? trimmed.slice(trimmed.indexOf(":") + 1).trim()
      : realPos;
    if (!realPos || standard.has(realPos)) continue;
    out.push({ pos: realPos, label: label || realPos, custom: true });
  }
  return out;
};

const computeOptions = async (folder: string): Promise<PosOption[]> => {
  // Which standard positions actually have art in this background?
  const checks = await Promise.all(
    STANDARD_POSITIONS.map(async (p) => ({
      p,
      exists: await imageExists(folder, [p.pos, p.legacy]),
    })),
  );
  let standard: PosOption[] = checks
    .filter((c) => c.exists)
    .map((c) => ({ pos: c.p.pos, label: c.p.label }));

  // AO2 falls back to the witness stand when a background defines no positions.
  if (standard.length === 0) {
    standard = [{ pos: "wit", label: "Witness" }];
  }

  let custom: PosOption[] = [];
  try {
    custom = parseCustomPositions(await request(`${folder}design.ini`));
  } catch {
    // no design.ini -> standard positions only
  }

  return [...standard, ...custom];
};

/**
 * Rebuilds the Role dropdown for the current background, mirroring AO2-Client:
 * only standard positions whose art exists are listed, custom positions from
 * design.ini are appended, and the witness stand is the fallback when a
 * background defines nothing. The current selection is preserved when it's
 * still valid, otherwise it defaults to witness.
 */
export const updatePositionsFromDesignIni = async (bgName: string) => {
  const roleSelect = document.getElementById(
    "role_select",
  ) as HTMLSelectElement | null;
  if (!roleSelect || !bgName) return;

  const folder = `${AO_HOST}background/${encodeURI(bgName.toLowerCase())}/`;

  let options = cache.get(folder);
  if (!options) {
    options = await computeOptions(folder);
    cache.set(folder, options);
  }

  const previous = roleSelect.value;
  roleSelect.innerHTML = "";
  roleSelect.add(new Option("Default", ""));
  for (const o of options) {
    const opt = new Option(o.label, o.pos);
    if (o.custom) opt.dataset.custompos = "1";
    roleSelect.add(opt);
  }

  const values = Array.from(roleSelect.options).map((o) => o.value);
  if (values.includes(previous)) {
    roleSelect.value = previous;
  } else if (values.includes("wit")) {
    roleSelect.value = "wit";
  } else {
    roleSelect.value = "";
  }
};
